import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';

export type UserType = 'influencer' | 'advertiser' | null;

export interface SocialMediaMetrics {
  followers: number;
  engagementRate: number;
}

export interface InfluencerProfile {
  bio: string;
  instagramHandle: string;
  youtubeHandle?: string;
  youtubeChannelName?: string;
  youtubeChannelId?: string;
  youtubeThumbnailUrl?: string;
  facebookHandle: string;
  socialMediaProfiles: string[];
  totalFollowers: number;
  averageEngagementRate: number;
  categories: string[];
  totalCampaigns: number;
  rating: number;
  totalRatings: number;
  location: string;
  languages: string[];
  preferredBudget: {
    amount: number;
    currency: string;
  };
  availability: boolean;
  socialMediaMetrics: {
    instagram: SocialMediaMetrics;
    youtube: SocialMediaMetrics;
    facebook: SocialMediaMetrics;
  };
  profileStatus: 'draft' | 'complete';
}

export interface AdvertiserProfile {
  bio: string;
  companyName: string;
  companyWebsite: string;
  industry: string[];
  totalCampaigns: number;
  rating: number;
  totalRatings: number;
  location: string;
  contactEmail: string;
  contactPhone: string;
}

export interface ProfileState {
  userType: UserType;
  name: string | null;
  email: string | null;
  isAuthenticated: boolean;
  avatar: string | null;
  profileStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  influencerProfile?: InfluencerProfile;
  advertiserProfile?: AdvertiserProfile;
  error: string | null;
}

const initialState: ProfileState = {
  userType: null,
  name: null,
  email: null,
  isAuthenticated: false,
  avatar: null,
  profileStatus: 'idle',
  influencerProfile: undefined,
  advertiserProfile: undefined,
  error: null
};

// Async thunk for setting profile data
export const setProfile = createAsyncThunk(
  'profile/setProfile',
  async (profileData: Partial<ProfileState>, { rejectWithValue }) => {
    try {
      // If we're setting a new profile (not just updating state), make API call
      if (profileData.isAuthenticated && !profileData.influencerProfile && !profileData.advertiserProfile) {
        const response = await api.get('/api/auth/me');
        const { data: user } = response.data;
        return {
          ...profileData,
          userType: user.role,
          name: user.username,
          email: user.email,
          avatar: user.profileImage || null,
          influencerProfile: user.role === 'influencer' ? user.influencerProfile : undefined,
          advertiserProfile: user.role === 'advertiser' ? user.advertiserProfile : undefined
        };
      }
      // Otherwise just return the provided data (for state updates)
      return profileData;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearProfile: (state) => {
      Object.assign(state, initialState);
    },
    updateProfileStatus: (state, action: PayloadAction<'idle' | 'loading' | 'succeeded' | 'failed'>) => {
      state.profileStatus = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(setProfile.pending, (state) => {
        state.profileStatus = 'loading';
        state.error = null;
      })
      .addCase(setProfile.fulfilled, (state, action) => {
        state.profileStatus = 'succeeded';
        // Merge the new profile data into state
        Object.assign(state, action.payload);
      })
      .addCase(setProfile.rejected, (state, action) => {
        state.profileStatus = 'failed';
        state.error = action.payload as string || 'Failed to update profile';
      });
  }
});

export const { clearProfile, updateProfileStatus, setError } = profileSlice.actions;

export default profileSlice.reducer;

export const updateProfile = createAsyncThunk(
  'profile/updateProfile',
  async (profileData: any, { dispatch }) => {
    try {
      const response = await api.patch('/api/auth/profile', profileData);
      const { user } = response.data.data;

      // Update profile in Redux store
      dispatch(setProfile({
        userType: user.role,
        name: user.username,
        email: user.email,
        isAuthenticated: true,
        avatar: user.profileImage || null,
        influencerProfile: user.role === 'influencer' ? {
          bio: user.influencerProfile?.bio || '',
          instagramHandle: user.influencerProfile?.instagramHandle || '',
          youtubeHandle: user.influencerProfile?.youtubeHandle || '',
          youtubeChannelName: user.influencerProfile?.youtubeChannelName || '',
          youtubeChannelId: user.influencerProfile?.youtubeChannelId || '',
          youtubeThumbnailUrl: user.influencerProfile?.youtubeThumbnailUrl || '',
          facebookHandle: user.influencerProfile?.facebookHandle || '',
          socialMediaProfiles: user.influencerProfile?.socialMediaProfiles || [],
          totalFollowers: user.influencerProfile?.totalFollowers || 0,
          averageEngagementRate: user.influencerProfile?.averageEngagementRate || 0,
          categories: user.influencerProfile?.categories || [],
          totalCampaigns: user.influencerProfile?.totalCampaigns || 0,
          rating: user.influencerProfile?.rating || 0,
          totalRatings: user.influencerProfile?.totalRatings || 0,
          location: user.influencerProfile?.location || '',
          languages: user.influencerProfile?.languages || [],
          preferredBudget: user.influencerProfile?.preferredBudget || { amount: 0, currency: 'USD' },
          availability: user.influencerProfile?.availability ?? true,
          socialMediaMetrics: user.influencerProfile?.socialMediaMetrics || {
            instagram: { followers: 0, engagementRate: 0 },
            youtube: { followers: 0, engagementRate: 0 },
            facebook: { followers: 0, engagementRate: 0 }
          },
          profileStatus: user.influencerProfile?.profileStatus || 'complete'
        } : undefined,
        advertiserProfile: user.role === 'advertiser' ? user.advertiserProfile : undefined
      }));

      return response.data;
    } catch (error: any) {
      throw error;
    }
  }
); 